import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { scoreTextClass } from 'components/ui/status-badge';
import { formatGroupedNumber } from 'lib/dateFormat';
import { useTestCategoryLabel } from 'hooks/use-test-category-label';
import { cn } from 'lib/utils';
import type { TestPerfRow } from 'modules/analytics';
import { AdvancedUpgradeNotice } from './AdvancedUpgradeNotice';

interface Props {
  tests?: TestPerfRow[];
  isLoading: boolean;
  hasAdvanced: boolean;
  /** Opens the Tests & questions tab, which lists every test. */
  onShowAll: () => void;
}

/** The overview lists the busiest tests; the full table lives on its own tab. */
const MAX_ROWS = 8;

/**
 * Per-test results for the period (PRO tests endpoint). The endpoint has no
 * last-activity time per test, so that column is left out.
 */
export const TestResultsTable: React.FC<Props> = ({ tests, isLoading, hasAdvanced, onShowAll }) => {
  const { t } = useTranslation();
  const categoryLabel = useTestCategoryLabel();
  const rows = (tests ?? []).slice(0, MAX_ROWS);
  const hiddenCount = (tests?.length ?? 0) - rows.length;

  const head = (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead>{t('analytics_test')}</TableHead>
        <TableHead className="hidden w-40 md:table-cell">{t('category')}</TableHead>
        <TableHead className="w-32 text-right">{t('attempts')}</TableHead>
        <TableHead className="w-32 text-right">{t('avg_score')}</TableHead>
        <TableHead className="w-32 text-right">{t('pass_rate')}</TableHead>
      </TableRow>
    </TableHeader>
  );

  const renderBody = () => {
    if (!hasAdvanced) return <AdvancedUpgradeNotice />;
    if (isLoading) {
      return (
        <Table className="table-fixed">
          {head}
          <TableBody>
            {[0, 1, 2, 3].map((i) => (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell>
                  <Skeleton className={cn('h-3.5', i % 2 ? 'w-2/5' : 'w-1/2')} />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <Skeleton className="h-3.5 w-16" />
                </TableCell>
                {[0, 1, 2].map((c) => (
                  <TableCell key={c}>
                    <Skeleton className="ml-auto h-3.5 w-10" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    if (rows.length === 0) {
      return <EmptyState icon={<BarChart3 />} title={t('no_tests_yet')} description={t('no_activity_this_week')} />;
    }
    return (
      <Table className="table-fixed">
        {head}
        <TableBody>
          {rows.map((row) => {
            const graded = row.graded > 0;
            return (
              <TableRow key={row.testId} data-test-result-row={row.testId}>
                <TableCell className="max-w-0">
                  <Link
                    to={`/app/tests/${row.testId}/results`}
                    className="block truncate text-[13.5px] font-medium text-foreground hover:underline"
                  >
                    {row.title}
                  </Link>
                </TableCell>
                <TableCell className="hidden max-w-0 text-[13px] text-foreground/80 md:table-cell">
                  {categoryLabel(row.category) ? (
                    <span className="block truncate" title={categoryLabel(row.category)}>
                      {categoryLabel(row.category)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-[13px] tabular-nums">{formatGroupedNumber(row.total)}</TableCell>
                <TableCell className="text-right text-[13px]">
                  {graded ? (
                    <span className={cn('font-semibold tabular-nums', scoreTextClass(row.avgScore))}>{row.avgScore}%</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-[13px] tabular-nums">
                  {graded ? `${row.passRate}%` : <span className="text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <section className="flex flex-col gap-2.5" aria-labelledby="analytics-test-results">
      <h2 id="analytics-test-results" className="text-sm font-semibold text-foreground">
        {t('test_results')}
      </h2>
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {renderBody()}
        {hasAdvanced && !isLoading && hiddenCount > 0 && (
          <div className="border-t border-border px-4 py-2.5">
            <button
              type="button"
              onClick={onShowAll}
              className="rounded-sm text-[13px] font-medium text-foreground/80 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('view_all')} <span aria-hidden>→</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestResultsTable;
